import React, { useState } from "react";
import { Clock, Users, Heart, TrendingUp, ArrowRight, Calendar } from "lucide-react";
import { Button } from "./ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";

const StoriesSection = () => {
  const [activeYear, setActiveYear] = useState(35);

  // Cost of Delay calculations
  const calculateCorpus = (monthlyInvestment, years, rate = 12) => {
    const monthlyRate = rate / 100 / 12;
    const months = years * 12;
    return monthlyInvestment * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) * (1 + monthlyRate);
  };

  const rahulData = {
    startAge: 25,
    monthly: 10000,
    years: 35,
    totalInvested: 10000 * 12 * 35,
    corpus: calculateCorpus(10000, 35),
  };

  const amitData = {
    startAge: 35,
    monthly: 20000,
    years: 25,
    totalInvested: 20000 * 12 * 25,
    corpus: calculateCorpus(20000, 25),
  };

  const formatCurrency = (value) => {
    if (value >= 10000000) {
      return `₹${(value / 10000000).toFixed(2)} Cr`;
    } else if (value >= 100000) {
      return `₹${(value / 100000).toFixed(2)} L`;
    }
    return `₹${value.toLocaleString("en-IN")}`;
  };

  const timelineYears = [25, 30, 35, 40, 45, 50, 55, 60];

  const getCorpusAtAge = (age) => {
    const rahulYears = Math.max(0, age - 25);
    const amitYears = Math.max(0, age - 35);
    return {
      rahul: rahulYears > 0 ? calculateCorpus(10000, rahulYears) : 0,
      amit: amitYears > 0 ? calculateCorpus(20000, amitYears) : 0,
    };
  };

  const currentData = getCorpusAtAge(activeYear);

  return (
    <section
      id="stories"
      className="py-24 sm:py-32 bg-gradient-to-b from-emerald-50/50 to-white"
      data-testid="stories-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-emerald-600 font-semibold mb-4">
            <Users className="w-4 h-4" />
            Real Stories, Real Lessons
          </span>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-semibold leading-snug text-emerald-950 mb-4">
            The Stories That Changed How People Think About Money
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            These aren't hypothetical scenarios. These are patterns we've seen play out 
            thousands of times with real families.
          </p>
        </div>

        <Tabs defaultValue="delay" className="w-full">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-12 bg-emerald-100 p-1 rounded-full">
            <TabsTrigger 
              value="delay" 
              className="rounded-full data-[state=active]:bg-white data-[state=active]:text-emerald-700 data-[state=active]:shadow-md"
              data-testid="tab-cost-of-delay"
            >
              <Clock className="w-4 h-4 mr-2" />
              Cost of Delay
            </TabsTrigger>
            <TabsTrigger 
              value="shield" 
              className="rounded-full data-[state=active]:bg-white data-[state=active]:text-emerald-700 data-[state=active]:shadow-md"
              data-testid="tab-emergency-shield"
            >
              <Heart className="w-4 h-4 mr-2" />
              Emergency Shield
            </TabsTrigger>
          </TabsList>

          {/* Cost of Delay Story */}
          <TabsContent value="delay" className="mt-0">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
              {/* Story Narrative */}
              <div className="glass-card rounded-3xl p-8 shadow-xl" data-testid="cost-of-delay-story">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-emerald-950">The Cost of Delay</h3>
                    <p className="text-sm text-slate-500">A tale of two friends</p>
                  </div>
                </div>

                <div className="prose prose-emerald">
                  <p className="text-slate-700 leading-relaxed mb-4">
                    <strong className="text-emerald-700">Rahul</strong> and <strong className="text-amber-600">Amit</strong> were 
                    college friends who graduated together at 25. Both got similar jobs with similar salaries.
                  </p>
                  
                  <div className="bg-emerald-50 rounded-xl p-4 mb-4 border-l-4 border-emerald-500">
                    <p className="text-emerald-800 font-medium mb-1">Rahul's Decision:</p>
                    <p className="text-emerald-700 text-sm">
                      "I'll start small but start NOW. Even ₹10,000/month will compound over time."
                    </p>
                  </div>

                  <div className="bg-amber-50 rounded-xl p-4 mb-4 border-l-4 border-amber-500">
                    <p className="text-amber-800 font-medium mb-1">Amit's Decision:</p>
                    <p className="text-amber-700 text-sm">
                      "I'll wait until I'm settled. At 35, I'll invest ₹20,000/month – double what Rahul invests!"
                    </p>
                  </div>

                  <p className="text-slate-700 leading-relaxed">
                    Fast forward to age 60. Both retire. Amit invested <strong>₹60 Lakhs</strong> (double Rahul's ₹42 Lakhs). 
                    But who has the bigger retirement corpus?
                  </p>
                </div>

                <div className="mt-6 p-4 bg-emerald-900 rounded-xl text-white">
                  <p className="text-sm text-emerald-200 mb-2">THE VERDICT AT AGE 60</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-emerald-300 text-xs">Rahul (Started at 25)</p>
                      <p className="text-2xl font-bold">{formatCurrency(rahulData.corpus)}</p>
                      <p className="text-xs text-emerald-400">Invested: {formatCurrency(rahulData.totalInvested)}</p>
                    </div>
                    <div>
                      <p className="text-amber-300 text-xs">Amit (Started at 35)</p>
                      <p className="text-2xl font-bold">{formatCurrency(amitData.corpus)}</p>
                      <p className="text-xs text-amber-400">Invested: {formatCurrency(amitData.totalInvested)}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Interactive Timeline */}
              <div className="glass-card rounded-3xl p-8 shadow-xl" data-testid="delay-timeline">
                <h4 className="text-lg font-semibold text-emerald-950 mb-6">
                  Watch Their Wealth Grow Over Time
                </h4>

                {/* Timeline Slider */}
                <div className="mb-8">
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-slate-500">Age</span>
                    <span className="text-lg font-bold text-emerald-600">{activeYear} years</span>
                  </div>
                  <div className="flex gap-2">
                    {timelineYears.map((year) => (
                      <button
                        key={year}
                        onClick={() => setActiveYear(year)}
                        className={`flex-1 py-2 text-xs font-medium rounded-lg transition-all ${
                          activeYear === year
                            ? "bg-emerald-500 text-white shadow-lg"
                            : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                        }`}
                        data-testid={`timeline-year-${year}`}
                      >
                        {year}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Comparison Bars */}
                <div className="space-y-6">
                  {/* Rahul's Bar */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                          <span className="text-emerald-700 font-bold text-sm">R</span>
                        </div>
                        <span className="font-medium text-slate-700">Rahul</span>
                        <span className="text-xs text-slate-500">
                          (₹10K/mo since age 25)
                        </span>
                      </div>
                      <span className="font-bold text-emerald-600" data-testid="rahul-corpus">
                        {formatCurrency(currentData.rahul)}
                      </span>
                    </div>
                    <div className="h-8 bg-emerald-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-full transition-all duration-500"
                        style={{
                          width: `${Math.min(100, (currentData.rahul / Math.max(currentData.rahul, currentData.amit, 1)) * 100)}%`,
                        }}
                      />
                    </div>
                  </div>

                  {/* Amit's Bar */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                          <span className="text-amber-700 font-bold text-sm">A</span>
                        </div>
                        <span className="font-medium text-slate-700">Amit</span>
                        <span className="text-xs text-slate-500">
                          (₹20K/mo since age 35)
                        </span>
                      </div>
                      <span className="font-bold text-amber-600" data-testid="amit-corpus">
                        {formatCurrency(currentData.amit)}
                      </span>
                    </div>
                    <div className="h-8 bg-amber-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-amber-400 to-amber-600 rounded-full transition-all duration-500"
                        style={{
                          width: `${Math.min(100, (currentData.amit / Math.max(currentData.rahul, currentData.amit, 1)) * 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Key Insight */}
                <div className="mt-8 p-4 bg-gradient-to-r from-emerald-50 to-amber-50 rounded-xl border border-emerald-100">
                  <p className="text-sm text-slate-600 mb-1">Key Insight</p>
                  <p className="font-semibold text-emerald-900">
                    {activeYear >= 60 
                      ? `Rahul invested ₹18 Lakhs LESS but ended up with ₹${((rahulData.corpus - amitData.corpus) / 10000000).toFixed(1)} Crore MORE!`
                      : activeYear < 35
                      ? "Amit hasn't started yet. Every year of delay costs him crores."
                      : `Even with double the investment, Amit is ₹${formatCurrency(Math.max(0, currentData.rahul - currentData.amit))} behind.`
                    }
                  </p>
                </div>

                <div className="mt-6 text-center">
                  <p className="text-2xl font-bold text-emerald-700 mb-2">
                    Time is the greatest multiplier.
                  </p>
                  <p className="text-slate-500">Your future self will thank you for starting today.</p>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Emergency Shield Story */}
          <TabsContent value="shield" className="mt-0">
            <div className="max-w-4xl mx-auto">
              <div className="glass-card rounded-3xl p-8 md:p-12 shadow-xl" data-testid="emergency-shield-story">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-8 items-center">
                  {/* Story Content */}
                  <div className="md:col-span-3">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 bg-rose-500 rounded-full flex items-center justify-center">
                        <Heart className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-emerald-950">The Emergency Shield</h3>
                        <p className="text-sm text-slate-500">The Sharma Family's Story</p>
                      </div>
                    </div>

                    <div className="space-y-4 text-slate-700 leading-relaxed">
                      <p>
                        The Sharmas were a happy middle-class family from Pune. Mr. Sharma, 48, had been 
                        diligently investing ₹15,000 monthly in mutual funds for 12 years. Their corpus 
                        had grown to <strong className="text-emerald-600">₹52 Lakhs</strong> – their 
                        daughter's wedding fund, their retirement cushion, their life's savings.
                      </p>

                      <p>
                        Then came the diagnosis: <strong className="text-rose-600">Stage 2 cancer</strong>. 
                        The treatment cost? <strong>₹18 Lakhs</strong> over 8 months.
                      </p>

                      <div className="bg-rose-50 rounded-xl p-4 border-l-4 border-rose-500">
                        <p className="text-rose-800 font-medium mb-2">Without Health Insurance:</p>
                        <p className="text-rose-700 text-sm">
                          They would have had to redeem ₹18 Lakhs from their mutual funds – 35% of their 
                          life's savings gone. The daughter's wedding postponed. Retirement plans shattered.
                        </p>
                      </div>

                      <div className="bg-emerald-50 rounded-xl p-4 border-l-4 border-emerald-500">
                        <p className="text-emerald-800 font-medium mb-2">With Health Insurance (₹25L cover):</p>
                        <p className="text-emerald-700 text-sm">
                          Their ₹15,000/year health policy covered the entire treatment. Their mutual 
                          fund corpus stayed intact. Mr. Sharma recovered, and 6 months later, they 
                          celebrated their daughter's wedding as planned.
                        </p>
                      </div>

                      <p className="font-medium text-emerald-900">
                        "That ₹15,000 annual premium saved our ₹52 Lakh corpus. It wasn't an expense – 
                        it was a shield."
                      </p>
                    </div>
                  </div>

                  {/* Visual Summary */}
                  <div className="md:col-span-2">
                    <div className="bg-gradient-to-br from-emerald-900 to-emerald-800 rounded-2xl p-6 text-white">
                      <p className="text-emerald-300 text-sm uppercase tracking-wider mb-4">The Math</p>
                      
                      <div className="space-y-4">
                        <div className="flex justify-between items-center pb-3 border-b border-emerald-700">
                          <span className="text-emerald-200">Health Insurance Premium</span>
                          <span className="font-bold">₹15,000/yr</span>
                        </div>
                        <div className="flex justify-between items-center pb-3 border-b border-emerald-700">
                          <span className="text-emerald-200">Treatment Cost</span>
                          <span className="font-bold">₹18,00,000</span>
                        </div>
                        <div className="flex justify-between items-center pb-3 border-b border-emerald-700">
                          <span className="text-emerald-200">MF Corpus Saved</span>
                          <span className="font-bold text-emerald-400">₹52,00,000</span>
                        </div>
                        <div className="flex justify-between items-center pt-2">
                          <span className="text-emerald-200">ROI on Premium</span>
                          <span className="font-bold text-2xl text-amber-400">120x</span>
                        </div>
                      </div>

                      <div className="mt-6 pt-4 border-t border-emerald-700">
                        <p className="text-sm text-emerald-200">
                          Health Insurance isn't a cost. It's a <strong className="text-white">Wealth Protection Strategy</strong>.
                        </p>
                      </div>
                    </div>

                    <Button
                      asChild
                      className="w-full mt-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full"
                      data-testid="insurance-quote-cta"
                    >
                      <a href="#contact">
                        Get Your Shield Today
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default StoriesSection;
